import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, NavigationStart, NavigationEnd, NavigationError, Router } from '@angular/router';
import { LangService } from './services/lang.service';
import { ViewportScroller } from '@angular/common';
import { HeaderComponent } from './shared/header/header.component';
import { FooterComponent } from './shared/footer/footer.component';
import { SctollToTopComponent } from './shared/scroll-to-top/scroll-to-top.component';
import { SpinnerComponent } from './shared/spinner/spinner.component';
import { Keyboard } from '@capacitor/keyboard';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { HttpClient } from '@angular/common/http';
import { catchError, of } from 'rxjs';
import { AppVersionService } from './services/app-version.service';
import { SweetAlertService } from './services/sweetAlert.service';

//TODO: revisar los literales y ponerlos en el es.json

@Component({
    selector: 'app-root',
    imports: [
        CommonModule,
        RouterModule,
        HeaderComponent,
        FooterComponent,
        SctollToTopComponent,
        SpinnerComponent
    ],
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css']
})
export class AppComponent implements OnInit {
  title = 'DMDonantesMMC';
  isIos: boolean = false;
  showHeader = true;
  showFooter = true;
  canLoadApp: boolean = false;
  // private readonly testCurrentAppVersion: string = '1.0.2';
  private currentAppVersion: string = 'unknown';
  private recommendedUpdateShown: boolean = false;
  private appStoreUrl: string = '';
  private playStoreUrl: string = '';
  private excludedRoutes = ['/forget-password', '/reset-password', '/activate-account','/breadcrubms'];

  private langService = inject(LangService);
  private router = inject(Router);
  private http = inject(HttpClient);
  private appVersionService = inject(AppVersionService);
  private sweetAlertService = inject(SweetAlertService);

  constructor(private viewportScroller: ViewportScroller) {
    this.langService.initializeLang();

    if (Capacitor.getPlatform() === 'ios') {
      Keyboard.setResizeMode({ mode: 'body' as any }); // iOS
    } else if (Capacitor.getPlatform() === 'android') {
      Keyboard.setResizeMode({ mode: 'none' as any }); // Android
    }

    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.showHeader = !this.excludedRoutes.includes(event.url);
        this.showFooter = !this.excludedRoutes.includes(event.url);
      }
      if (event instanceof NavigationEnd) {
        this.viewportScroller.scrollToPosition([0, 0]);
      }
    });
  }

  ngOnInit(): void {
    if (Capacitor.isNativePlatform()) {
      this.loadAppVersion();
    } else {
      this.canLoadApp = true;
    }
    this.initializeDeviceFlags();
    this.subscribeToNavigationErrors();
    this.hideInitialLoader();
  }

  private loadAppVersion(): void {
    // if (this.testCurrentAppVersion) {
    //   this.currentAppVersion = this.testCurrentAppVersion;
    //   this.appVersionService.setVersion(this.testCurrentAppVersion);
    //   this.loadAppVersionsFromApi();
    //   return;
    // }

    this.http.get<{ version: string }>('assets/app-version.json').pipe(
      catchError(() => of({ version: 'unknown' }))
    ).subscribe(({ version }) => {
      this.currentAppVersion = version;
      this.appVersionService.setVersion(version);
      this.loadAppVersionsFromApi();
    });
  }

  private loadAppVersionsFromApi(): void {
    this.appVersionService.getAppVersions().subscribe({
      next: async (response) => {
        this.canLoadApp = await this.validateVersions(response);
      },
      error: (error) => {
        console.error('❌ Error app-versions:', error);
        this.canLoadApp = false;
      }
    });
  }

  private async validateVersions(response: any): Promise<boolean> {
    const versionData = response?.AppVersion?.[0];
    const minVersion = versionData?.minVersion;
    const recommendedVersion = versionData?.recommendedVersion;
    this.appStoreUrl = versionData?.appStoreUrl || '';
    this.playStoreUrl = versionData?.playStoreUrl || '';

    if (!minVersion && !recommendedVersion) {
      console.warn('⚠️ No se encontraron minVersion/recommendedVersion en la respuesta de app-versions');
      return true;
    }

    if (minVersion === '0') {
      this.sweetAlertService.showBlocker({
        title: 'App no disponible temporalmente',
        text: 'Por favor, inténtelo en unos minutos'
      });
      return false;
    }

    if (minVersion) {
      const minComparison = this.compareVersions(this.currentAppVersion, minVersion);
      this.logVersionComparison('minVersion', minVersion, minComparison);

      if (minComparison < 0) {
        await this.showRequiredUpdateModal();
        return false;
      }
    }

    if (recommendedVersion) {
      const recommendedComparison = this.compareVersions(this.currentAppVersion, recommendedVersion);
      this.logVersionComparison('recommendedVersion', recommendedVersion, recommendedComparison);

      if (recommendedComparison < 0 && !this.recommendedUpdateShown && !sessionStorage.getItem('recommendedUpdateShown')) {
        const Swal = (await import('sweetalert2')).default;
        await Swal.fire({
          icon: 'warning',
          title: 'Actualización disponible',
          text: `Tienes a tu disposición una versión superior de la App de Málaga No Caduca.`,
          confirmButtonText: 'Actualizar',
          cancelButtonText: 'Cancelar',
          showCancelButton: true,
          allowOutsideClick: false,
          allowEscapeKey: false,
          preConfirm: async () => {
            this.recommendedUpdateShown = true;
            sessionStorage.setItem('recommendedUpdateShown', 'true');
            await this.openStore();
            return true;
          }
        }).then((result) => {
          if (result.isDismissed) {
            this.recommendedUpdateShown = true;
            sessionStorage.setItem('recommendedUpdateShown', 'true');
          }
        });
        return true;
      }
    }

    return true;
  }

  private async showRequiredUpdateModal(): Promise<void> {
    const Swal = (await import('sweetalert2')).default;
    await Swal.fire({
      icon: 'error',
      title: 'Actualización requerida',
      text: `Por favor actualiza la App de Málaga No Caduca a la última versión.`,
      confirmButtonText: 'Actualizar',
      allowOutsideClick: false,
      allowEscapeKey: false,
      showCloseButton: false,
      preConfirm: async () => {
        await this.openStore();
        Swal.update({
          html: 'Por favor, actualiza la App de Málaga No Caduca para poder seguir usándola.',
        });
        return false; // evita que el modal se cierre
      }
    });
  }

  private async openStore(): Promise<void> {
    const platform = Capacitor.getPlatform();
    const url = platform === 'ios' ? this.appStoreUrl : this.playStoreUrl;
    if (url) {
      await Browser.open({ url });
    } else {
      console.warn('⚠️ URLs de tienda no disponibles');
    }
  }

  private logVersionComparison(type: 'minVersion' | 'recommendedVersion', targetVersion: string, comparison: number): void {
    const messagesByType = {
      minVersion: {
        lower: 'La versión actual está por debajo de la mínima',
        equal: 'La versión actual es igual a la mínima',
        higher: 'La versión actual está por encima de la mínima'
      },
      recommendedVersion: {
        lower: 'La versión actual está por debajo de la recomendada',
        equal: 'La versión actual es igual a la recomendada',
        higher: 'La versión actual está por encima de la recomendada'
      }
    };

    const message = comparison < 0
      ? messagesByType[type].lower
      : comparison === 0
        ? messagesByType[type].equal
        : messagesByType[type].higher;

  }

  private initializeDeviceFlags(): void {
    this.isIos = /iPad|iPhone|iPod/.test(navigator.userAgent);
  }

  private subscribeToNavigationErrors(): void {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationError) {
        this.router.navigate(['/noauthorization']);
      }
    });
  }

  private hideInitialLoader(): void {
    const loadingElement = document.getElementById('loading-init');
    if (!loadingElement) {
      return;
    }

    setTimeout(() => {
      loadingElement.style.transition = 'opacity 0.5s ease';
      loadingElement.style.opacity = '0';

      setTimeout(() => {
        loadingElement.style.display = 'none';
      }, 500);
    }, 500);
  }

  private compareVersions(current: string, target: string): number {
    const normalize = (value: string): number[] =>
      value
        .replace(/^v/i, '')
        .split('.')
        .map((part) => Number(part.replace(/\D/g, '')) || 0);

    const currentParts = normalize(current);
    const targetParts = normalize(target);
    const maxLength = Math.max(currentParts.length, targetParts.length);

    for (let index = 0; index < maxLength; index++) {
      const currentValue = currentParts[index] ?? 0;
      const targetValue = targetParts[index] ?? 0;

      if (currentValue > targetValue) {
        return 1;
      }

      if (currentValue < targetValue) {
        return -1;
      }
    }

    return 0;
  }
}